/**
 * Firebase App Check for X9 web PWAs (qr.x9.app, do.x9.app).
 *
 * Requires a reCAPTCHA v3 or Enterprise site key registered in the Firebase
 * Console (App Check → Web app → reCAPTCHA). Pass via build-time env:
 *
 *   VITE_FIREBASE_APP_CHECK_RECAPTCHA_SITE_KEY
 *
 * Local dev: register a debug token in Console and set
 *   VITE_FIREBASE_APP_CHECK_DEBUG_TOKEN=<uuid from console>
 * or `true` to auto-print a token in the browser console on first load.
 */
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
  ReCaptchaV3Provider,
  type AppCheck,
} from 'firebase/app-check';
import type { FirebaseApp } from 'firebase/app';

let _appCheck: AppCheck | null = null;
let _initAttempted = false;

function configureDebugToken(): void {
  if (typeof window === 'undefined') return;
  const debug = import.meta.env.VITE_FIREBASE_APP_CHECK_DEBUG_TOKEN;
  if (!debug) return;

  const g = globalThis as typeof globalThis & {
    FIREBASE_APPCHECK_DEBUG_TOKEN?: string | boolean;
  };
  g.FIREBASE_APPCHECK_DEBUG_TOKEN = debug === 'true' ? true : debug;
}

/**
 * Initialise App Check once per page load. No-op when the site key env var
 * is missing (local dev without Console setup).
 */
export function initFirebaseAppCheck(app: FirebaseApp): AppCheck | null {
  if (_appCheck) return _appCheck;
  if (_initAttempted) return null;
  _initAttempted = true;

  const siteKey = import.meta.env.VITE_FIREBASE_APP_CHECK_RECAPTCHA_SITE_KEY?.trim();
  if (!siteKey) {
    if (import.meta.env.DEV) {
      console.warn(
        '[X9] App Check not initialized — set VITE_FIREBASE_APP_CHECK_RECAPTCHA_SITE_KEY (see docs/platforms/web-setup.md)'
      );
    }
    return null;
  }

  configureDebugToken();

  const providerKind = import.meta.env.VITE_FIREBASE_APP_CHECK_PROVIDER ?? 'v3';
  const provider =
    providerKind === 'enterprise'
      ? new ReCaptchaEnterpriseProvider(siteKey)
      : new ReCaptchaV3Provider(siteKey);

  _appCheck = initializeAppCheck(app, {
    provider,
    isTokenAutoRefreshEnabled: true,
  });

  return _appCheck;
}
